<template>
  <div class="ai-kg">
    <section class="ai-page-head">
      <div class="ai-page-head__crumb">
        <router-link to="/data-visualization">시각화</router-link>
        <span>›</span>
        <em>지식그래프</em>
      </div>
      <h2 class="ai-page-head__title">지식그래프</h2>
      <p class="ai-page-head__sub">
        선택한 계열의 공정 항목(상위)–속성(하위) 구조와, 실험 데이터에서 계산한 속성 간 상관관계를 그래프로 탐색합니다.
        노드를 드래그해 배치를 바꾸고, 클릭하면 상세 정보와 연결 관계를 볼 수 있습니다.
      </p>
      <div class="ai-data-banner">
        <b>허브 실험입력 데이터</b>(작업본 최신) 기반입니다. 값이 없는 항목은 그래프에 나타나지 않습니다.
      </div>
    </section>

    <!-- 계열 선택 -->
    <section class="ai-series">
      <span class="ai-series__label">계열 선택</span>
      <button
        v-for="s in seriesList"
        :key="s.key"
        type="button"
        class="ai-series__btn"
        :class="{ 'is-on': s.key === seriesKey, 'is-off': s.disabled }"
        :disabled="s.disabled"
        :title="s.disabled ? '데이터 없음' : s.processName"
        @click="selectSeries(s.key)"
      >{{ s.name }}<small v-if="s.key === seriesKey && data"> · {{ data.rowCount }}건</small></button>
      <span v-if="loading" class="ai-series__loading">데이터 로딩 중…</span>
      <span v-else-if="loadError" class="ai-series__error">{{ loadError }}</span>
    </section>

    <section class="ai-kg__toolbar" v-if="data">
      <input
        v-model.trim="query"
        type="text"
        class="ai-kg__search"
        placeholder="노드 검색 (항목·속성 이름)"
      />
      <div class="ai-kg__legend">
        <span class="ai-kg__legend-item"><span class="dot" style="background:#003f67"></span>계열</span>
        <span class="ai-kg__legend-item"><span class="dot" style="background:#0dbde9"></span>공정 항목(상위)</span>
        <span class="ai-kg__legend-item"><span class="dot" style="background:#e8803c"></span>속성(하위)</span>
        <span class="ai-kg__legend-item"><span class="line"></span>구조</span>
        <span class="ai-kg__legend-item"><span class="line line--corr"></span>상관 |r|≥{{ corrTh.toFixed(2) }}</span>
      </div>
      <label class="ai-kg__th">
        상관 임계값
        <input type="range" min="0.3" max="0.95" step="0.05" v-model.number="corrTh" />
        <b>{{ corrTh.toFixed(2) }}</b>
      </label>
      <button type="button" class="ai-kg__reset" @click="resetView">보기 초기화</button>
    </section>

    <section class="ai-kg__body" v-if="data">
      <div class="ai-kg__canvas" ref="canvas">
        <svg
          class="ai-kg__svg"
          @wheel.prevent="onWheel"
          @pointerdown="onBgDown"
          @pointermove="onMove"
          @pointerup="onUp"
          @pointerleave="onUp"
        >
          <g :transform="`translate(${view.x},${view.y}) scale(${view.k})`">
            <!-- 간선 -->
            <g v-for="e in visibleEdges" :key="e.id">
              <line
                :x1="nodeMap[e.source].x" :y1="nodeMap[e.source].y"
                :x2="nodeMap[e.target].x" :y2="nodeMap[e.target].y"
                class="ai-kg__edge"
                :class="{ 'is-corr': e.kind === 'corr', 'is-neg': e.kind === 'corr' && e.r < 0, 'is-hl': isEdgeHl(e) }"
                :style="e.kind === 'corr' ? { strokeWidth: 1 + Math.abs(e.r) * 3 } : null"
              />
              <text
                v-if="e.kind === 'corr'"
                :x="(nodeMap[e.source].x + nodeMap[e.target].x) / 2"
                :y="(nodeMap[e.source].y + nodeMap[e.target].y) / 2 - 4"
                class="ai-kg__edge-lbl"
                :class="{ 'is-hl': isEdgeHl(e) }"
                text-anchor="middle"
              >{{ e.r.toFixed(2) }}</text>
            </g>
            <!-- 노드 -->
            <g
              v-for="n in nodes"
              :key="n.id"
              class="ai-kg__node"
              :class="{ 'is-dim': isDimmed(n), 'is-sel': selectedId === n.id }"
              :transform="`translate(${n.x},${n.y})`"
              @pointerdown.stop="onNodeDown($event, n)"
              @click.stop="selectNode(n)"
            >
              <circle :r="n.r" :fill="n.color" />
              <circle :r="n.r" class="ai-kg__node-ring" />
              <text :y="n.r + 13" text-anchor="middle" class="ai-kg__node-lbl">{{ n.label }}</text>
            </g>
          </g>
        </svg>
        <div class="ai-kg__hint">마우스 휠: 확대/축소 · 빈 곳 드래그: 이동 · 노드 드래그: 재배치</div>
      </div>

      <!-- 상세 패널 -->
      <aside class="ai-kg__detail">
        <template v-if="selectedNode">
          <div class="ai-kg__detail-head">
            <span class="dot" :style="{ background: selectedNode.color }"></span>
            <h3>{{ selectedNode.label }}</h3>
            <em>{{ kindName(selectedNode.kind) }}</em>
          </div>
          <div class="ai-kg__detail-desc">
            <template v-if="selectedNode.kind === 'prop'">
              <p>상위항목: <b>{{ selectedNode.groupName }}</b></p>
              <p v-if="selectedNode.unit">단위: <b>{{ selectedNode.unit }}</b></p>
              <p>유효 데이터: <b>{{ selectedNode.validCount }}</b>건
                <span v-if="selectedNode.mean !== undefined"> · 평균 {{ fmt(selectedNode.mean) }} · 표준편차 {{ fmt(selectedNode.std) }}</span></p>
            </template>
            <template v-else-if="selectedNode.kind === 'group'">
              <p>속성 {{ selectedNode.propCount }}개 (값 보유 기준)</p>
            </template>
            <template v-else>
              <p>{{ data.processName }}</p>
              <p>시료 행 {{ data.rowCount }}건 · 공정 항목 {{ groupNodes.length }}개 · 분석 속성 {{ propNodes.length }}개</p>
            </template>
          </div>
          <h4 class="ai-kg__detail-sub">연결 관계 ({{ selectedEdges.length }})</h4>
          <ul class="ai-kg__detail-list">
            <li v-for="e in selectedEdges" :key="e.id" @click="selectNode(nodeMap[otherEnd(e)])">
              <b>{{ nodeMap[otherEnd(e)].label }}</b>
              <span class="rel" :class="{ neg: e.kind === 'corr' && e.r < 0 }">
                {{ e.kind === 'corr' ? 'r=' + e.r.toFixed(2) : '소속' }}
              </span>
            </li>
          </ul>
        </template>
        <template v-else>
          <p class="ai-kg__detail-empty">
            노드를 클릭하면<br />상세 정보가 표시됩니다.
          </p>
        </template>
      </aside>
    </section>
  </div>
</template>

<script>
import { SERIES, resolveSeries, loadSeries, pearson } from "./lib/dvData";

const COLORS = { series: "#003f67", group: "#0dbde9", prop: "#e8803c" };

export default {
  name: "DvKnowledgeGraph",
  data() {
    return {
      seriesKey: null,
      seriesProc: null,
      data: null,
      loading: false,
      loadError: null,
      corrTh: 0.6,
      nodes: [],
      structEdges: [],
      corrEdgesAll: [], // 임계값 미적용 전체 (|r|>=0.3)
      query: "",
      selectedId: null,
      view: { x: 0, y: 0, k: 1 },
      drag: null,
      simTicks: 0,
      rafId: null,
      size: { w: 900, h: 560 },
    };
  },
  computed: {
    seriesList() {
      return SERIES.map((s) => ({
        ...s,
        disabled: this.seriesProc ? !this.seriesProc[s.key] : false,
        processName: this.seriesProc && this.seriesProc[s.key] ? this.seriesProc[s.key].processName : "",
      }));
    },
    nodeMap() {
      const m = {};
      this.nodes.forEach((n) => (m[n.id] = n));
      return m;
    },
    groupNodes() { return this.nodes.filter((n) => n.kind === "group"); },
    propNodes() { return this.nodes.filter((n) => n.kind === "prop"); },
    corrEdges() {
      return this.corrEdgesAll.filter((e) => Math.abs(e.r) >= this.corrTh);
    },
    visibleEdges() {
      return [...this.structEdges, ...this.corrEdges].filter(
        (e) => this.nodeMap[e.source] && this.nodeMap[e.target]
      );
    },
    selectedNode() {
      return this.selectedId ? this.nodeMap[this.selectedId] : null;
    },
    selectedEdges() {
      if (!this.selectedId) return [];
      return this.visibleEdges.filter((e) => e.source === this.selectedId || e.target === this.selectedId);
    },
    matchedIds() {
      if (!this.query) return null;
      const q = this.query.toLowerCase();
      const set = new Set();
      this.nodes.forEach((n) => {
        if (n.label.toLowerCase().includes(q)) set.add(n.id);
      });
      return set;
    },
  },
  async mounted() {
    const q = String(this.$route.query.series || "").toUpperCase();
    this.seriesKey = SERIES.some((s) => s.key === q) ? q : SERIES[0].key;
    try { this.seriesProc = await resolveSeries(this.$wApi); } catch (e) { /* 아래 load 에서 처리 */ }
    this.measure();
    window.addEventListener("resize", this.measure);
    await this.load();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.measure);
    if (this.rafId) cancelAnimationFrame(this.rafId);
  },
  methods: {
    fmt(v) {
      if (v === undefined || v === null) return "-";
      const a = Math.abs(v);
      if (a >= 1000) return Math.round(v).toLocaleString();
      if (a >= 1) return v.toFixed(2);
      return v.toFixed(4);
    },
    kindName(k) {
      return k === "series" ? "계열" : k === "group" ? "공정 항목" : "속성";
    },
    async selectSeries(key) {
      if (key === this.seriesKey) return;
      this.seriesKey = key;
      if (String(this.$route.query.series || "").toUpperCase() !== key) {
        this.$router.replace({ query: { ...this.$route.query, series: key } });
      }
      await this.load();
    },
    async load() {
      this.loading = true;
      this.loadError = null;
      this.selectedId = null;
      this.query = "";
      try {
        const d = await loadSeries(this.$wApi, this.seriesKey);
        if (!d) { this.loadError = "해당 계열의 공정도를 찾지 못했습니다."; this.data = null; return; }
        this.data = d;
        this.buildGraph();
        this.$nextTick(() => { this.measure(); this.centerView(); this.startSim(280); });
      } catch (e) {
        this.loadError = "데이터 로딩 실패: " + ((e && e.message) || e);
        this.data = null;
      } finally {
        this.loading = false;
      }
    },
    buildGraph() {
      const d = this.data;
      const nodes = [];
      const structEdges = [];
      const cx = 450, cy = 280;

      nodes.push({
        id: "series", kind: "series", label: d.seriesName,
        color: COLORS.series, r: 26, x: cx, y: cy, vx: 0, vy: 0, fixed: false,
      });

      // 상위항목: 분석 가능한(활성) 속성이 있는 그룹만 노드로 (요구 ③)
      const groups = d.groups
        .map((g) => ({ ...g, activeProps: g.props.filter((p) => p.active) }))
        .filter((g) => g.activeProps.length > 0);

      groups.forEach((g, gi) => {
        const ga = (2 * Math.PI * gi) / groups.length;
        nodes.push({
          id: "g" + g.id, kind: "group", label: g.name, propCount: g.activeProps.length,
          color: COLORS.group, r: 14 + Math.min(8, g.activeProps.length),
          x: cx + 170 * Math.cos(ga), y: cy + 170 * Math.sin(ga), vx: 0, vy: 0, fixed: false,
        });
        structEdges.push({ id: "se_s_g" + g.id, kind: "struct", source: "series", target: "g" + g.id });
        g.activeProps.forEach((p, pi) => {
          const pa = ga + ((pi - g.activeProps.length / 2) * 0.25);
          nodes.push({
            id: "p" + p.id, kind: "prop",
            label: p.name + (p.unit ? ` (${p.unit})` : ""),
            groupName: g.name, unit: p.unit, validCount: p.validCount, mean: p.mean, std: p.std,
            prop: p,
            color: COLORS.prop, r: 9 + Math.min(6, Math.floor(p.validCount / 30)),
            x: cx + 300 * Math.cos(pa), y: cy + 300 * Math.sin(pa), vx: 0, vy: 0, fixed: false,
          });
          structEdges.push({ id: "se_g" + g.id + "_p" + p.id, kind: "struct", source: "g" + g.id, target: "p" + p.id });
        });
      });

      // 상관 엣지(전체 쌍, |r|>=0.3 만 저장 — 임계 슬라이더로 필터)
      const props = groups.flatMap((g) => g.activeProps);
      const corrEdgesAll = [];
      for (let i = 0; i < props.length; i++) {
        for (let j = i + 1; j < props.length; j++) {
          const { r, n } = pearson(props[i], props[j]);
          if (r !== null && Math.abs(r) >= 0.3) {
            corrEdgesAll.push({
              id: "ce" + props[i].id + "_" + props[j].id, kind: "corr",
              source: "p" + props[i].id, target: "p" + props[j].id, r, n,
            });
          }
        }
      }

      this.nodes = nodes;
      this.structEdges = structEdges;
      this.corrEdgesAll = corrEdgesAll;
    },
    measure() {
      const el = this.$refs.canvas;
      if (el) this.size = { w: el.clientWidth, h: el.clientHeight };
    },
    centerView() {
      this.view = { x: this.size.w / 2 - 450, y: this.size.h / 2 - 280, k: Math.min(1, this.size.w / 1000) };
    },
    resetView() {
      this.query = "";
      this.selectedId = null;
      this.corrTh = 0.6;
      this.buildGraph();
      this.measure();
      this.centerView();
      this.startSim(280);
    },
    // ─── force 시뮬레이션 (반발 + 간선 스프링 + 중심 인력) ───
    startSim(ticks) {
      this.simTicks = Math.max(this.simTicks, ticks);
      if (!this.rafId) this.tick();
    },
    tick() {
      this.rafId = null;
      if (this.simTicks <= 0) return;
      this.simTicks--;
      const ns = this.nodes;
      const alpha = 0.25 + 0.5 * (this.simTicks / 280);
      for (let i = 0; i < ns.length; i++) {
        for (let j = i + 1; j < ns.length; j++) {
          const a = ns[i], b = ns[j];
          let dx = b.x - a.x, dy = b.y - a.y;
          let d2 = dx * dx + dy * dy;
          if (d2 < 1) { dx = Math.random() - 0.5; dy = Math.random() - 0.5; d2 = 1; }
          const d = Math.sqrt(d2);
          const f = (2200 * alpha) / d2;
          const fx = (dx / d) * f, fy = (dy / d) * f;
          if (!a.fixed) { a.vx -= fx; a.vy -= fy; }
          if (!b.fixed) { b.vx += fx; b.vy += fy; }
        }
      }
      // 구조 간선은 짧게, 상관 간선은 약하게 길게
      const spring = (e, len, k) => {
        const a = this.nodeMap[e.source], b = this.nodeMap[e.target];
        if (!a || !b) return;
        const dx = b.x - a.x, dy = b.y - a.y;
        const d = Math.max(1, Math.sqrt(dx * dx + dy * dy));
        const f = (d - len) * k * alpha;
        const fx = (dx / d) * f, fy = (dy / d) * f;
        if (!a.fixed) { a.vx += fx; a.vy += fy; }
        if (!b.fixed) { b.vx -= fx; b.vy -= fy; }
      };
      this.structEdges.forEach((e) => spring(e, e.source === "series" ? 150 : 90, 0.08));
      this.corrEdges.forEach((e) => spring(e, 140, 0.015));
      ns.forEach((n) => {
        if (n.fixed) { n.vx = 0; n.vy = 0; return; }
        n.vx += (450 - n.x) * 0.0024 * alpha * 4;
        n.vy += (280 - n.y) * 0.0024 * alpha * 4;
        n.vx *= 0.82; n.vy *= 0.82;
        n.x += n.vx; n.y += n.vy;
      });
      this.nodes = this.nodes.slice();
      this.rafId = requestAnimationFrame(this.tick);
    },
    // ─── 마우스 상호작용 ───
    toGraphXY(ev) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      return {
        x: (ev.clientX - rect.left - this.view.x) / this.view.k,
        y: (ev.clientY - rect.top - this.view.y) / this.view.k,
      };
    },
    onWheel(ev) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      const mx = ev.clientX - rect.left, my = ev.clientY - rect.top;
      const k2 = Math.max(0.3, Math.min(2.6, this.view.k * (ev.deltaY < 0 ? 1.12 : 0.89)));
      this.view = {
        k: k2,
        x: mx - ((mx - this.view.x) / this.view.k) * k2,
        y: my - ((my - this.view.y) / this.view.k) * k2,
      };
    },
    onBgDown(ev) {
      this.drag = { type: "bg", sx: ev.clientX, sy: ev.clientY, vx: this.view.x, vy: this.view.y };
    },
    onNodeDown(ev, n) {
      n.fixed = true;
      this.drag = { type: "node", node: n };
    },
    onMove(ev) {
      if (!this.drag) return;
      if (this.drag.type === "bg") {
        this.view = {
          ...this.view,
          x: this.drag.vx + (ev.clientX - this.drag.sx),
          y: this.drag.vy + (ev.clientY - this.drag.sy),
        };
      } else {
        const p = this.toGraphXY(ev);
        this.drag.node.x = p.x;
        this.drag.node.y = p.y;
        this.nodes = this.nodes.slice();
        this.startSim(30);
      }
    },
    onUp() {
      if (this.drag && this.drag.type === "node") {
        this.drag.node.fixed = false;
        this.startSim(60);
      }
      this.drag = null;
    },
    selectNode(n) {
      this.selectedId = n.id;
    },
    otherEnd(e) {
      return e.source === this.selectedId ? e.target : e.source;
    },
    isDimmed(n) {
      if (this.matchedIds) return !this.matchedIds.has(n.id);
      if (!this.selectedId) return false;
      if (n.id === this.selectedId) return false;
      return !this.selectedEdges.some((e) => e.source === n.id || e.target === n.id);
    },
    isEdgeHl(e) {
      return this.selectedId && (e.source === this.selectedId || e.target === this.selectedId);
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-kg {
  max-width: 1200px;
  margin: 0 auto;
  padding: 44px 20px 120px;
}

/* 공통 페이지 머리(연관관계 페이지와 동일 톤) */
.ai-page-head {
  margin-bottom: 22px;
  &__crumb {
    font-size: 13px;
    color: #789;
    margin-bottom: 10px;
    a {
      color: #789;
      text-decoration: none;
      &:hover { color: #003f67; text-decoration: underline; }
    }
    span { margin: 0 6px; }
    em { font-style: normal; color: #003f67; font-weight: 600; }
  }
  &__title {
    font-size: 26px;
    font-weight: 700;
    color: #111;
    letter-spacing: -0.5px;
  }
  &__sub {
    margin-top: 10px;
    font-size: 14px;
    color: #556;
    line-height: 22px;
  }
}
.ai-data-banner {
  margin-top: 14px;
  padding: 10px 14px;
  border: 1px solid #c9e7f5;
  background: #f2fafe;
  border-radius: 8px;
  font-size: 13px;
  color: #0a5f80;
  b { font-weight: 700; }
}

/* 계열 선택 (연관관계 페이지와 동일) */
.ai-series {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  &__label { font-size: 13px; font-weight: 700; color: #334; margin-right: 4px; }
  &__btn {
    height: 36px;
    padding: 0 18px;
    border: 1px solid #ccd5dd;
    border-radius: 18px;
    background: #fff;
    font-size: 14px;
    font-weight: 600;
    color: #556;
    cursor: pointer;
    small { font-weight: 400; font-size: 12px; }
    &.is-on { background: #003f67; border-color: #003f67; color: #fff; }
    &.is-off { opacity: 0.4; cursor: not-allowed; }
    &:not(.is-on):not(.is-off):hover { border-color: #0dbde9; }
  }
  &__loading { font-size: 13px; color: #0a93b5; }
  &__error { font-size: 13px; color: #c0392b; }
}

.ai-kg__toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.ai-kg__search {
  width: 240px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #ccd5dd;
  border-radius: 8px;
  font-size: 13px;
  &:focus { outline: none; border-color: #0dbde9; }
}
.ai-kg__legend {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.ai-kg__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: #334;
  padding: 4px 8px;
  .dot { width: 10px; height: 10px; border-radius: 50%; }
  .line { width: 18px; height: 2px; background: #b9c9d6; }
  .line--corr { height: 3px; background: #c73924; }
}
.ai-kg__th {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: #334;
  input[type="range"] { width: 120px; }
  b { color: #003f67; }
}
.ai-kg__reset {
  margin-left: auto;
  height: 34px;
  padding: 0 14px;
  border: 1px solid #003f67;
  border-radius: 8px;
  background: #fff;
  color: #003f67;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #003f67; color: #fff; }
}

.ai-kg__body {
  display: flex;
  gap: 20px;
  align-items: stretch;
}
.ai-kg__canvas {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  height: 560px;
  border: 1px solid #dde6ec;
  border-radius: 12px;
  background: #fbfdfe;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(9, 76, 177, 0.06);
}
.ai-kg__svg {
  width: 100%;
  height: 100%;
  display: block;
  cursor: grab;
  touch-action: none;
  &:active { cursor: grabbing; }
}
.ai-kg__hint {
  position: absolute;
  left: 12px;
  bottom: 10px;
  font-size: 11px;
  color: #9ab;
  pointer-events: none;
}

.ai-kg__edge {
  stroke: #c3d2de;
  stroke-width: 1.2;
  &.is-corr { stroke: #c73924; opacity: 0.55; }
  &.is-corr.is-neg { stroke: #175da2; }
  &.is-hl { opacity: 1; stroke-width: 3 !important; }
}
.ai-kg__edge-lbl {
  font-size: 9px;
  fill: #8aa;
  pointer-events: none;
  &.is-hl { fill: #334; font-weight: 700; }
}
.ai-kg__node {
  cursor: pointer;
  circle { transition: opacity 0.15s; }
  .ai-kg__node-ring {
    fill: none;
    stroke: #fff;
    stroke-width: 2;
  }
  &.is-sel .ai-kg__node-ring { stroke: #ffb300; stroke-width: 3.5; }
  &.is-dim { opacity: 0.18; }
}
.ai-kg__node-lbl {
  font-size: 10px;
  fill: #223;
  font-weight: 600;
  paint-order: stroke;
  stroke: #fbfdfe;
  stroke-width: 3;
  pointer-events: none;
}

.ai-kg__detail {
  flex: 0 0 280px;
  border: 1px solid #dde6ec;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(9, 76, 177, 0.06);
  padding: 20px;
  overflow-y: auto;
  max-height: 560px;
}
.ai-kg__detail-head {
  display: flex;
  align-items: center;
  gap: 8px;
  .dot { width: 12px; height: 12px; border-radius: 50%; flex: none; }
  h3 { font-size: 15px; font-weight: 700; color: #111; word-break: break-all; }
  em {
    margin-left: auto;
    font-style: normal;
    font-size: 11px;
    color: #789;
    border: 1px solid #e2e8ee;
    border-radius: 10px;
    padding: 2px 8px;
    flex: none;
  }
}
.ai-kg__detail-desc {
  margin-top: 12px;
  font-size: 13px;
  color: #445;
  line-height: 21px;
  b { font-weight: 700; }
}
.ai-kg__detail-sub {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #eef2f5;
  font-size: 13px;
  font-weight: 700;
  color: #003f67;
}
.ai-kg__detail-list {
  margin-top: 8px;
  li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 8px;
    border-radius: 6px;
    font-size: 12.5px;
    cursor: pointer;
    b { color: #223; font-weight: 600; word-break: break-all; }
    .rel { margin-left: auto; color: #c0392b; font-size: 12px; flex: none; }
    .rel.neg { color: #175da2; }
    &:hover { background: #f1f8fc; }
  }
}
.ai-kg__detail-empty {
  padding: 120px 0;
  text-align: center;
  color: #99a;
  font-size: 13px;
  line-height: 20px;
}

@media (max-width: 900px) {
  .ai-kg__body { flex-direction: column; }
  .ai-kg__detail { flex: none; max-height: 320px; }
}
</style>
